import { useEffect, useState } from "react";

type TimeCorrection = {
	appliedAt: string;
	minute: number;
} | null;

export function useCurrentMinute(
	startTime: string | null,
	halfTimeAt: string | null,
	secondHalfStartTime: string | null,
	timeCorrection: TimeCorrection,
): number | null {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 10_000);
		return () => clearInterval(interval);
	}, []);

	// Refresh immediately when match state changes (e.g. a fresh time
	// correction lands) instead of waiting up to 10s for the next tick.
	// Each component using this hook gets its own independent instance
	// with its own tick phase, so without this, two components showing
	// "the same" minute could visibly disagree for up to 10s after a change.
	useEffect(() => {
		setNow(Date.now());
	}, [startTime, halfTimeAt, secondHalfStartTime, timeCorrection]);

	if (!startTime) return null;
	if (halfTimeAt && !secondHalfStartTime) return null; // paused at HT — no live minute

	// Math.ceil + a floor clamp (matching use-match-minute.ts) rather than
	// Math.floor: kickoff should read as minute 1 immediately, not 0, and
	// small negative drift — clock skew, or `now` not having refreshed yet —
	// must never round down to a "-1m" flash or (worse, for a correction)
	// silently fall through to the uncorrected calculation below.
	if (timeCorrection) {
		const correctionMs = new Date(timeCorrection.appliedAt).getTime();
		return Math.max(
			timeCorrection.minute,
			timeCorrection.minute + Math.ceil((now - correctionMs) / 60_000),
		);
	}

	if (secondHalfStartTime) {
		return Math.max(
			46,
			Math.ceil((now - new Date(secondHalfStartTime).getTime()) / 60_000) + 45,
		);
	}
	return Math.max(1, Math.ceil((now - new Date(startTime).getTime()) / 60_000));
}
